import React, { useEffect, useState } from "react";
import {
  AddLogCommunicationIcon,
  CancellationRulesIcon,
  emailIcon,
  EmailIcon,
  LowRiskIcon,
  Meeting,
  Support,
  SupportIcons,
} from "../assets/icons";
// Import your existing SVG components
import { useDispatch, useSelector } from "react-redux";
import { reqToGetCommunicationList } from "../redux/slices.js/tenants/tenantsSlice";
import { useParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import PopUpModal from "../modals/PopUpModal";

// Icon mapping based on your existing components
const iconMap = {
  call: CancellationRulesIcon,
  email: EmailIcon,
  meeting: Meeting,
  default: SupportIcons, // default fallback
};

// Helper function to format API data
// Helper function to format API data
const formatCommunicationData = (apiData) => {
  // If no data or empty result, return dummy data
  if (!apiData || !apiData.result || apiData.result.length === 0) {
    return [
      {
        id: 1,
        type: "email",
        label: "Email",
        icon: emailIcon,
        title: "Welcome Communication",
        date: new Date().toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        }),
        description:
          "This is a sample communication entry. Start logging your communications to see them here.",
        participants: ["John Doe", "Jane Smith"],
        outcome: {
          text: "Outcome: Pending",
          icon: LowRiskIcon,
        },
      },
      {
        id: 2,
        type: "call",
        label: "Call",
        icon: CancellationRulesIcon,
        title: "Initial Consultation",
        date: new Date().toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        }),
        description:
          "Sample call entry. Add your actual communications to replace these examples.",
        participants: ["Mike Johnson"],
        outcome: {
          text: "Outcome: Scheduled",
          icon: LowRiskIcon,
        },
      },
    ];
  }

  return apiData.result.map((item) => {
    const type = item.type?.toLowerCase() || "email";

    // Get the appropriate icon component from your existing ones
    const IconComponent = iconMap[type] || iconMap.default;

    // Format date
    const date = new Date(item.createdAt).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });

    // Extract participant names
    const participants =
      item.participants?.map((participant) => participant.name) || [];

    return {
      id: item.id,
      type: type,
      label: type.charAt(0).toUpperCase() + type.slice(1), // Capitalize first letter
      icon: IconComponent, // Store the component, not the rendered element
      title: item.subject || "Communication",
      date: date,
      description: item.notes || "No description provided",
      participants: participants,
      outcome: {
        text: "Outcome: Completed",
        icon: LowRiskIcon, // Store the component, not the rendered element
      },
    };
  });
};

function ParticipantPill({ name }) {
  return (
    <span className="bg-gray-100 text-gray-700 rounded-lg px-[11px] py-[7px] text-xs font-medium mr-2 mb-1 inline-block">
      {name}
    </span>
  );
}

// Helper component to render icons safely
const IconRenderer = ({ icon: IconComponent, ...props }) => {
  if (!IconComponent) return null;

  // If it's already a React element, return it as is
  if (React.isValidElement(IconComponent)) {
    return IconComponent;
  }

  // If it's a React component, render it
  if (
    typeof IconComponent === "function" ||
    typeof IconComponent === "object"
  ) {
    return <IconComponent {...props} />;
  }

  return null;
};

export default function TimelineActivities() {
  const [showCommunicationModal, setShowCommunicationModal] = useState(false);

  // Define options for the form
  const CommunicationType = [
    { value: 'email', label: 'Email' },
    { value: 'call', label: 'Call' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'note', label: 'Note' },
  ];

  const participantsOptions = [
    { value: "John smith", label: "John smith" },
    { value: "Sarah Johnson", label: "Sarah Johnson" },
    { value: "Emily Jones", label: "Emily Jones" },
  ];

  // Define validation schema
  const communicationSchema = Yup.object().shape({
    type: Yup.string().required('Type is required'),
    subject: Yup.string().required('Subject is required'),
    description: Yup.string().required('Description is required'),
    participants: Yup.array().min(1, 'At least one participant is required'),
  });
  const dispatch = useDispatch();
  const { id } = useParams();
  const communicationList = useSelector(
    (state) => state.tenants.communicationList
  );

  // useEffect(() => {
  //   dispatch(
  //     reqToGetCommunicationList({
  //       id: id,
  //       onSuccess: () => {},
  //       onFailure: (err) => console.error(err),
  //     })
  //   );
  // }, [dispatch, id]);

  // Format API data
  const activities = formatCommunicationData(communicationList);

  return (
    <div className="chart-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-span !text-[20px] text-black !font-semibold mb-0 flex items-center justify-between mb-4 w-full !border-0">
          Communication timeline
          <button
            onClick={() => setShowCommunicationModal(true)}
            className="whitespace-nowrap btn-light-blue w-auto !gap-2">
            <IconRenderer icon={AddLogCommunicationIcon} />
            Log communication
          </button>
        </h2>
      </div>

      {activities.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No communication activities found.
        </div>
      ) : (
        activities.map((act, idx) => (
          <div key={act.id} className="last:mb-0 relative">
            <div className="absolute left-4 top-[20px] -bottom-[20px] w-[1px] bg-[#D3D6DA3B] z-0"></div>
            <div className="flex items-start md:gap-5 gap-2">
              <div className="bg-[#FFECEC] w-[34px] h-[34px] rounded-[30px] opacity-100 flex items-center justify-center z-9">
                <IconRenderer icon={act.icon} />
              </div>
              <div className="flex flex-col flex-1 items-start gap-2 border-b border-b-[#D3D6DA3B] mb-5 pb-5">
                <span
                  className={
                    act.type === "call"
                      ? "bg-green-100 px-3 py-1 rounded text-[12px] text-[#52C41A] font-medium mr-2"
                      : "bg-green-100 px-3 py-1 rounded text-[12px] text-[#52C41A] font-medium mr-2"
                  }
                >
                  {act.label}
                </span>
                <div className="flex items-baseline md:flex-row flex-col text-left">
                  <p className="font-semibold text-base mr-2 mb-0 pe-2 border-r border-r-[#D3D6DA3B]">
                    {act.title}
                  </p>
                  <span className="text-sm text-[#888076] font-normal">
                    {act.date}
                  </span>
                </div>
                <p className="text-[16px] text-[#B4B4B4] mb-0 text-left">
                  {act.description}
                </p>
                <div className="text-sm my-[18px] text-left">
                  <span className="font-semibold text-gray-900 pe-[9px] text-left">
                    Participants:
                  </span>
                  {act.participants && act.participants.length > 0 ? (
                    act.participants.map((name) => (
                      <ParticipantPill key={name} name={name} />
                    ))
                  ) : (
                    <span className="text-gray-500">No participants</span>
                  )}
                </div>
                <div
                  className={`inline-block bg-[#F8FFCF] mt-0 py-2 px-2 rounded-lg leading-[20px] text-[0.75rem] font-medium flex items-center text-[#4285F4] gap-1 text-left`}
                >
                  <IconRenderer icon={act.outcome.icon} />
                  {act.outcome.text}
                </div>
              </div>
            </div>
          </div>
        ))
      )}
      {/* Log Communication Modal */}
      <PopUpModal
        visible={showCommunicationModal}
        onClose={() => setShowCommunicationModal(false)}
        confirmText="Log Communication"
        title="Log Communication"
        size="medium"
        onConfirm={() => {
          const form = document.querySelector('form');
          if (form) {
            form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          }
        }}
      >
        <Formik
          initialValues={{
            type: 'email',
            subject: '',
            description: '',
            participants: []
          }}
          validationSchema={communicationSchema}
          onSubmit={(values, { setSubmitting }) => {
            console.log('Communication logged:', values);
            setSubmitting(false);
            setShowCommunicationModal(false);
          }}
        >
          {({ values, setFieldValue }) => (
            <Form className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="form-group">
                  <label>
                    Type <span>*</span>
                  </label>
                  <Select
                    options={CommunicationType}
                    defaultValue={CommunicationType[0]}
                    name="Type"
                    inputId="Type"
                    isClearable={false}
                    isSearchable={false}
                    menuPlacement="auto"
                  />
                  <ErrorMessage name="Type" component="div" className="text-red-500 text-sm" />
                </div>
                <div className="form-group">
                  <label>Subject <span>*</span></label>
                  <Field
                    type="text"
                    name="subject"
                    placeholder="Communication subject"
                    className="w-full p-2 border rounded"
                  />
                  <ErrorMessage name="subject" component="div" className="text-red-500 text-sm" />
                </div>
              </div>
              <div className="form-group">
                <label>Notes <span>*</span></label>
                <Field
                  as="textarea"
                  name="description"
                  rows={4}
                  placeholder="Communication details and notes"
                  className="w-full p-2 border rounded"
                />
                <ErrorMessage name="description" component="div" className="text-red-500 text-sm" />
              </div>
              <div className="form-group">
                <label>
                  Participants <span>*</span>
                </label>
                <Select
                  options={participantsOptions}
                  defaultValue={participantsOptions[0]}
                  name="participants"
                  inputId="participants"
                  isClearable={false}
                  isSearchable={false}
                  menuPlacement="auto"
                />
                <ErrorMessage name="participants" component="div" className="text-red-500 text-sm" />
              </div>
            </Form>
          )}
        </Formik>
      </PopUpModal>
    </div>
  );
}
